<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import Table from "../../../base-components/Table";
import { Menu } from "../../../base-components/Headless";
import Preview from "../../../base-components/Preview";
import { FormInput } from "../../../base-components/Form";
import LoadingIcon from "../../../base-components/LoadingIcon";
import { useProductosStore } from "../../../services/productos";
import { Producto } from "@fixeduy/restaurantestypes";
import Button from "../../../base-components/Button/Button.vue";
import { isAdmin } from "../../../utils/permisos";
import Lucide from "../../../base-components/Lucide/Lucide.vue";
import ModalProductosEditar from "./modals/ModalProductosEditar.vue";
import { useProductosCategoriasStore } from "../../../services/productosCategorias";

const productosStore = useProductosStore();

onBeforeMount(async () => {
  await productosStore.init();
});

const inputBuscar = ref("");
const productoSeleccionado = ref<Producto | null>(null);
const nombreActivo = ref("Ver inactivos");
const verInactivo = ref(false);
const categoriasStore = useProductosCategoriasStore();

const abrirEditar = (producto: Producto) => {
  productoSeleccionado.value = producto;
};

const cerrarModal = () => {
  productoSeleccionado.value = null;
};

const mostrarProducto = (producto: Producto) => {
  if (!verInactivo.value && !producto.activo) return false;

  const buscar = inputBuscar.value.toLocaleLowerCase().trim();
  if (buscar === "") return true;

  const nombre = (producto.nombre ?? "").toLocaleLowerCase();
  const codigo = String(producto.codigo ?? "").toLocaleLowerCase();
  return nombre.includes(buscar) || codigo.includes(buscar);
};

const productosVisibles = computed(() =>
  [...productosStore.productos]
    .filter(p => mostrarProducto(p))
    .sort((a, b) => (a.nombre ?? "").localeCompare(b.nombre ?? ""))
);

const mostrarEstadoVacio = computed(
  () =>
    !productosStore.cargando &&
    productosStore.productos.length > 0 &&
    productosVisibles.value.length === 0
);

const verInactivos = () => {
  verInactivo.value = !verInactivo.value;
  nombreActivo.value = verInactivo.value ? "Ver solo activos" : "Ver inactivos";
};
</script>

<template>
  <Preview class="mt-5 box" v-slot="{ toggle }">
    <div class="grid grid-cols-3 gap-4">
      <div class="col-span-2 p-5">
        <h2 class="text-base font-medium">Productos</h2>
        <FormInput
          class="mt-2"
          type="text"
          placeholder="Buscar por código o nombre..."
          v-model="inputBuscar"
        />
        <Button
          variant="secondary"
          rounded
          class="w-30 mb-2 mr-1 mt-4"
          @click="verInactivos"
        >
          {{ nombreActivo }}
        </Button>
      </div>
    </div>
    <div class="px-5 overflow-x-auto">
      <Preview.Panel>
        <div class="overflow-x-auto" v-if="!productosStore.cargando">
          <Table hover v-if="isAdmin()">
            <Table.Thead>
              <Table.Tr>
                <Table.Th class="whitespace-nowrap w-14"></Table.Th>
                <Table.Th class="whitespace-nowrap">Nombre</Table.Th>
                <Table.Th class="whitespace-nowrap">Categoría</Table.Th>
                <Table.Th class="whitespace-nowrap">Precio</Table.Th>
                <Table.Th class="whitespace-nowrap text-end">Acciones</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              <Table.Tr
                v-for="producto in productosVisibles"
                :key="producto.id"
                class="cursor-pointer"
                @click="abrirEditar(producto)"
              >
                <Table.Td class="w-14">
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-100 dark:border-darkmode-400 dark:bg-darkmode-600"
                  >
                    <img
                      v-if="producto.fotoUrl"
                      :src="producto.fotoUrl"
                      :alt="producto.nombre"
                      class="h-full w-full object-cover"
                    />
                    <Lucide
                      v-else
                      icon="Image"
                      class="h-5 w-5 text-slate-400"
                    />
                  </div>
                </Table.Td>
                <Table.Td :class="{ 'text-danger': !producto.activo }">{{
                  producto.nombre
                }}</Table.Td>
                <Table.Td :class="{ 'text-danger': !producto.activo }">{{
                  categoriasStore.getCategoriaById(producto.categoriaId!)
                    ?.nombre ?? ""
                }}</Table.Td>
                <Table.Td :class="{ 'text-danger': !producto.activo }">{{
                  producto.precioConIva
                }}</Table.Td>
                <Table.Td>
                  <div class="flex justify-end">
                    <Menu>
                      <Menu.Button :as="Button" class="px-2 !box" @click.stop>
                        <span class="flex items-right justify-center w-5 h-5">
                          <Lucide icon="Plus" class="w-4 h-4" />
                        </span>
                      </Menu.Button>
                      <Menu.Items class="w-40" placement="left-end">
                        <Menu.Item @click.stop="abrirEditar(producto)">
                          <Lucide icon="Edit2" class="w-4 h-4 mr-2" />
                          Editar
                        </Menu.Item>
                      </Menu.Items>
                    </Menu>
                  </div>
                </Table.Td>
              </Table.Tr>
              <Table.Tr
                v-if="
                  !productosStore.cargando && productosVisibles.length === 0
                "
              >
                <Table.Td colspan="30" class="text-center py-8 text-slate-500">
                  {{
                    mostrarEstadoVacio
                      ? "Ningún producto coincide con la búsqueda."
                      : "No hay productos para mostrar."
                  }}
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
          <div
            v-else
            class="text-center p-4 bg-red-100 border border-red-500 rounded-md text-red-600 m-5"
          >
            <Lucide
              icon="AlertCircle"
              class="w-6 h-6 mx-auto mb-2 text-red-600"
            />
            <p class="font-medium">No tienes permisos para ver esta página.</p>
          </div>
        </div>
        <div v-else>
          <div class="w-8 mx-auto">
            <LoadingIcon icon="oval" class="w-8 h-8" />
            <div class="w-auto mx-auto mt-2">Cargando...</div>
          </div>
        </div>
      </Preview.Panel>
    </div>
    <ModalProductosEditar
      v-if="productoSeleccionado"
      :producto="productoSeleccionado"
      @cerrar="cerrarModal"
    />
  </Preview>
</template>
